/**
 * AdminSupportTickets — internal support ticket triage console.
 *
 * Lets the platform owner / support team:
 *   • Browse all support tickets in a sortable, searchable table.
 *   • Filter by status, type, date range.
 *   • Drill into a single ticket (full description, device, source page).
 *   • Update status, priority, and add internal admin notes.
 *
 * Companion to the lightweight summary table on AdminDashboard.jsx.
 * Internal admin tool — English-only, intentionally outside the i18n
 * surface (same convention as the other /admin/* pages).
 */
/* eslint-disable */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft, LifeBuoy, RefreshCw, Search, Filter, CheckCircle2, Clock,
  AlertTriangle, XCircle, ExternalLink, Mail, Smartphone, FileText, Save,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TONES = {
  open: { bg: 'bg-amber-100', text: 'text-amber-800', icon: AlertTriangle, label: 'Open' },
  investigating: { bg: 'bg-sky-100', text: 'text-sky-800', icon: Clock, label: 'Investigating' },
  resolved: { bg: 'bg-emerald-100', text: 'text-emerald-800', icon: CheckCircle2, label: 'Resolved' },
  closed: { bg: 'bg-slate-100', text: 'text-slate-700', icon: XCircle, label: 'Closed' },
};

const PRIORITY_TONES = {
  high: 'bg-rose-100 text-rose-800 border-rose-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-slate-100 text-slate-700 border-slate-200',
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const fmtRelative = (iso) => {
  if (!iso) return '';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return ''; }
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_TONES[status] || STATUS_TONES.open;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${cfg.bg} ${cfg.text}`}
      data-testid={`ticket-status-${status}`}
    >
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const cls = PRIORITY_TONES[priority] || PRIORITY_TONES.low;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide border ${cls}`}
      data-testid={`ticket-priority-${priority || 'low'}`}
    >
      {priority || 'low'}
    </span>
  );
};

const AdminSupportTickets = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // Deep-link target — when arriving from the dashboard widget the URL
  // carries ?id=<uuid>; we auto-open the detail dialog for that ticket
  // once the list resolves. Cleared from the URL on dialog close so a
  // refresh doesn't keep reopening it.
  const targetTicketId = searchParams.get('id');

  // ----- Filter state -----
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sinceDays, setSinceDays] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);

  // ----- Data -----
  const [tickets, setTickets] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ----- Detail dialog state -----
  const [activeTicket, setActiveTicket] = useState(null);
  const [editStatus, setEditStatus] = useState('open');
  const [editPriority, setEditPriority] = useState('low');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Defensive role gate.
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Debounce search input → searchQuery (300ms).
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 200 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.type = typeFilter;
      if (sinceDays !== 'all') params.since_days = sinceDays;
      if (searchQuery) params.search = searchQuery;
      const res = await axios.get(`${API}/admin/support-tickets`, {
        params, withCredentials: true,
      });
      setTickets(res.data?.items || []);
      setStatusCounts(res.data?.status_counts || {});
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError('Please sign in as an admin.');
      else if (status === 403) setError('Admin access required.');
      else setError('Could not load support tickets.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, sinceDays, searchQuery]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets, refreshTick]);

  // Deep-link: if arriving with ?id=<uuid> (from the dashboard "Recent
  // support tickets" widget), find that ticket in the loaded list and
  // pop the detail dialog automatically. If the ticket isn't in the
  // current filtered page, fetch it directly so the dialog still opens.
  useEffect(() => {
    if (!targetTicketId) return;
    if (activeTicket && activeTicket.id === targetTicketId) return;
    const inList = tickets.find((t) => t.id === targetTicketId);
    if (inList) {
      openDetail(inList);
      return;
    }
    // Not in current page — fetch the single ticket directly. We hit the
    // admin list endpoint with the full id as a search term; if the
    // backend can't find it we silently no-op rather than throw.
    if (tickets.length > 0) {
      (async () => {
        try {
          const res = await axios.get(
            `${API}/admin/support-tickets?limit=200`,
            { withCredentials: true },
          );
          const all = res.data?.items || [];
          const t = all.find((x) => x.id === targetTicketId);
          if (t) openDetail(t);
        } catch { /* silent */ }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetTicketId, tickets]);

  const openDetail = (ticket) => {
    setActiveTicket(ticket);
    setEditStatus(ticket.status || 'open');
    setEditPriority(ticket.priority || 'low');
    setEditNotes(ticket.admin_notes || '');
  };

  const closeDetail = () => {
    if (saving) return;
    setActiveTicket(null);
    // Strip the deep-link id from the URL so a refresh doesn't re-pop
    // the dialog the user just dismissed.
    if (searchParams.get('id')) {
      const next = new URLSearchParams(searchParams);
      next.delete('id');
      setSearchParams(next, { replace: true });
    }
  };

  const saveTicket = async () => {
    if (!activeTicket) return;
    setSaving(true);
    try {
      const payload = {
        status: editStatus,
        priority: editPriority,
        admin_notes: editNotes,
      };
      const res = await axios.patch(
        `${API}/admin/support-tickets/${activeTicket.id}`,
        payload,
        { withCredentials: true },
      );
      const updated = res.data;
      setTickets((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      // Refresh status counts (cheap re-fetch is fine).
      setRefreshTick((n) => n + 1);
      setActiveTicket(updated);
      toast.success('Ticket updated');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not update ticket.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const quickResolve = async (ticket, e) => {
    e?.stopPropagation();
    try {
      const res = await axios.patch(
        `${API}/admin/support-tickets/${ticket.id}`,
        { status: 'resolved' },
        { withCredentials: true },
      );
      const updated = res.data;
      setTickets((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      setRefreshTick((n) => n + 1);
      toast.success('Marked resolved');
    } catch {
      toast.error('Could not mark resolved.');
    }
  };

  const summaryChips = useMemo(() => ([
    { key: 'open', label: 'Open', count: statusCounts.open || 0, tone: 'bg-amber-50 text-amber-800 border-amber-200' },
    { key: 'investigating', label: 'Investigating', count: statusCounts.investigating || 0, tone: 'bg-sky-50 text-sky-800 border-sky-200' },
    { key: 'resolved', label: 'Resolved', count: statusCounts.resolved || 0, tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
    { key: 'closed', label: 'Closed', count: statusCounts.closed || 0, tone: 'bg-slate-50 text-slate-700 border-slate-200' },
  ]), [statusCounts]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6">
        <p className="text-sm font-medium text-gray-900 mb-3" data-testid="support-tickets-error">{error}</p>
        <Button variant="outline" onClick={() => navigate('/admin')} data-testid="support-tickets-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to admin
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-support-tickets-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 md:top-16 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin')}
              aria-label="Back to admin"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <LifeBuoy className="h-4 w-4 text-amber-600" />
                <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">Support Tickets</h1>
              </div>
              <p className="text-[11px] text-gray-500">Triage, resolve, and add internal notes</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRefreshTick((t) => t + 1)}
            data-testid="refresh-tickets-btn"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Refresh
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24">
        {/* Summary chips */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="status-summary-chips">
          {summaryChips.map((c) => (
            <span
              key={c.key}
              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium ${c.tone}`}
              data-testid={`summary-chip-${c.key}`}
            >
              <span>{c.label}</span>
              <span className="tabular-nums font-semibold">{c.count}</span>
            </span>
          ))}
        </div>

        {/* Filters */}
        <Card className="border border-gray-100 rounded-2xl shadow-sm mb-5">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search title, message, email, name…"
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="status-filter-all">All statuses</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="investigating">Investigating</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="type-filter">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="problem_report">Problem reports</SelectItem>
                  <SelectItem value="contact_support">Contact support</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sinceDays} onValueChange={setSinceDays}>
                <SelectTrigger data-testid="date-filter">
                  <SelectValue placeholder="Date range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All time</SelectItem>
                  <SelectItem value="1">Last 24 hours</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tickets table */}
        <Card className="border border-gray-100 rounded-2xl shadow-sm" data-testid="tickets-table-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Filter className="h-4 w-4" /> Tickets
              <span className="text-gray-400 font-normal">({tickets.length})</span>
            </CardTitle>
            <CardDescription className="text-xs">
              {loading ? 'Loading…' : tickets.length === 0 ? 'No tickets match these filters.' : 'Click a row to triage'}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {tickets.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-gray-500 text-left text-xs">
                    <tr>
                      <th className="font-medium py-2 pr-3">Status</th>
                      <th className="font-medium py-2 pr-3">Subject</th>
                      <th className="font-medium py-2 pr-3 hidden md:table-cell">From</th>
                      <th className="font-medium py-2 pr-3 hidden lg:table-cell">Type</th>
                      <th className="font-medium py-2 pr-3">Priority</th>
                      <th className="font-medium py-2 pr-3 hidden md:table-cell">Created</th>
                      <th className="font-medium py-2 pr-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((t) => (
                      <tr
                        key={t.id}
                        className="border-t border-gray-50 hover:bg-slate-50 cursor-pointer"
                        onClick={() => openDetail(t)}
                        data-testid={`ticket-row-${t.id}`}
                      >
                        <td className="py-3 pr-3"><StatusBadge status={t.status || 'open'} /></td>
                        <td className="py-3 pr-3 max-w-[280px]">
                          <p className="font-medium text-gray-900 truncate">{t.title || '(no subject)'}</p>
                          <p className="text-xs text-gray-500 truncate">{(t.description || '').slice(0, 120)}</p>
                        </td>
                        <td className="py-3 pr-3 hidden md:table-cell">
                          <p className="text-gray-800 truncate max-w-[180px]">{t.contact_name || '—'}</p>
                          <p className="text-xs text-gray-500 truncate max-w-[180px]">{t.contact_email || '—'}</p>
                        </td>
                        <td className="py-3 pr-3 hidden lg:table-cell text-gray-700 capitalize">
                          {(t.type || '').replace('_', ' ')}
                        </td>
                        <td className="py-3 pr-3"><PriorityBadge priority={t.priority} /></td>
                        <td className="py-3 pr-3 hidden md:table-cell whitespace-nowrap text-xs text-gray-500">
                          <p>{fmtDateTime(t.created_at)}</p>
                          <p className="text-gray-400">{fmtRelative(t.created_at)}</p>
                        </td>
                        <td className="py-3 pr-3 text-right">
                          {(t.status || 'open') !== 'resolved' && (t.status || 'open') !== 'closed' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => quickResolve(t, e)}
                              data-testid={`quick-resolve-${t.id}`}
                            >
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                              Resolve
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); openDetail(t); }}
                              data-testid={`view-ticket-${t.id}`}
                            >
                              View
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail / triage dialog */}
      <Dialog open={!!activeTicket} onOpenChange={(o) => { if (!o) closeDetail(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ticket-detail-dialog">
          {activeTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 pr-8">
                  <StatusBadge status={activeTicket.status || 'open'} />
                  <span className="truncate">{activeTicket.title || '(no subject)'}</span>
                </DialogTitle>
                <DialogDescription>
                  Reference <span className="font-mono">#{(activeTicket.id || '').slice(0, 8).toUpperCase()}</span>
                  {' · '}{fmtDateTime(activeTicket.created_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-gray-500 uppercase tracking-wide text-[10px] mb-1">From</p>
                  <p className="font-medium text-gray-900">{activeTicket.contact_name || '—'}</p>
                  <p className="text-gray-600 flex items-center gap-1 mt-0.5">
                    <Mail className="h-3 w-3" />
                    {activeTicket.contact_email ? (
                      <a
                        href={`mailto:${activeTicket.contact_email}`}
                        className="text-emerald-700 hover:underline truncate"
                      >
                        {activeTicket.contact_email}
                      </a>
                    ) : '—'}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-gray-500 uppercase tracking-wide text-[10px] mb-1">Source</p>
                  <p className="font-medium text-gray-900 capitalize">
                    {(activeTicket.type || '').replace('_', ' ')} · {activeTicket.category || '—'}
                  </p>
                  <p className="text-gray-600 truncate mt-0.5 font-mono text-[11px]">
                    {activeTicket.source_page || '—'}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                  <FileText className="h-3 w-3" /> Message
                </Label>
                <div
                  className="mt-1 whitespace-pre-wrap bg-slate-50 rounded-lg p-3 text-sm text-gray-800 border border-slate-100 max-h-48 overflow-y-auto"
                  data-testid="ticket-detail-message"
                >
                  {activeTicket.description || <em className="text-gray-400">No message body</em>}
                </div>
              </div>

              {activeTicket.device_info && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-500 flex items-center gap-1 hover:text-gray-700">
                    <Smartphone className="h-3 w-3" /> Device & technical details
                  </summary>
                  <pre className="mt-2 bg-slate-50 rounded-lg p-3 text-[11px] text-gray-700 overflow-x-auto max-h-32">
{JSON.stringify(activeTicket.device_info, null, 2)}
                  </pre>
                </details>
              )}

              <div className="border-t border-gray-100 pt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="edit-status" className="text-xs">Status</Label>
                  <Select value={editStatus} onValueChange={setEditStatus}>
                    <SelectTrigger id="edit-status" data-testid="edit-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="investigating">Investigating</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-priority" className="text-xs">Priority</Label>
                  <Select value={editPriority} onValueChange={setEditPriority}>
                    <SelectTrigger id="edit-priority" data-testid="edit-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="edit-notes" className="text-xs">
                  Internal notes <span className="text-gray-400">(only visible to admins)</span>
                </Label>
                <Textarea
                  id="edit-notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Add a triage note — e.g. who responded, what was tried, follow-up date…"
                  rows={4}
                  className="mt-1"
                  data-testid="edit-notes-textarea"
                />
              </div>

              {activeTicket.resolved_at && (
                <p className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
                  Resolved {fmtDateTime(activeTicket.resolved_at)}
                  {activeTicket.resolved_by ? ` · by ${activeTicket.resolved_by.slice(0, 8)}` : ''}
                </p>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={closeDetail} disabled={saving} data-testid="cancel-edit-btn">
                  Close
                </Button>
                {activeTicket.contact_email && (
                  <a
                    href={`mailto:${activeTicket.contact_email}?subject=Re: ${encodeURIComponent(activeTicket.title || 'Your HealthLoop support request')} (Ticket #${(activeTicket.id || '').slice(0, 8).toUpperCase()})`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button variant="outline" data-testid="reply-via-email-btn">
                      <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                      Reply via email
                    </Button>
                  </a>
                )}
                <Button onClick={saveTicket} disabled={saving} data-testid="save-ticket-btn">
                  <Save className="h-3.5 w-3.5 mr-1.5" />
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSupportTickets;
